import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Badge } from '@vaiu/ui/badge';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { 
  Search, 
  MapPin, 
  Store, 
  Users, 
  Gift, 
  Zap, 
  TrendingUp,
  Star,
  CheckCircle,
  ArrowRight,
  QrCode,
  CreditCard,
  Shield,
  Globe,
  MessageSquare,
  Trophy,
  Target,
  Sparkles,
  Heart,
  Building,
  Phone,
  Mail,
  ExternalLink,
  Play,
  Award,
  Coins,
  Share2,
  UserPlus,
  ChevronRight,
  Lightbulb,
  Megaphone,
  HandHeart
} from 'lucide-react';

interface MerchantSuggestion {
  id: string;
  name: string;
  category: string;
  location: string;
  requestCount: number;
  estimatedRevenue: number;
  distance?: string;
}

interface SuccessStory {
  id: string;
  merchantName: string;
  category: string;
  requestCount: number;
  timeToOnboard: string;
  advocateName: string;
  rewardEarned: number;
  image?: string;
}

interface NetworkStats {
  totalMerchants: number;
  totalAdvocates: number;
  totalRequests: number;
  successfulOnboardings: number;
  totalRewardsEarned: number;
}

const MarketingSection: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [location, setLocation] = useState('');
  const [activeSection, setActiveSection] = useState<'discover' | 'rewards' | 'community'>('discover');

  // Mock data - these would normally come from props or API
  const merchantSuggestions: MerchantSuggestion[] = [
    {
      id: '1',
      name: 'Local Coffee Roasters',
      category: 'Food & Beverage',
      location: 'Downtown',
      requestCount: 47,
      estimatedRevenue: 2500,
      distance: '0.3 miles'
    },
    {
      id: '2',
      name: 'Green Garden Market',
      category: 'Grocery',
      location: 'Midtown',
      requestCount: 32,
      estimatedRevenue: 1800,
      distance: '0.7 miles'
    },
    {
      id: '3',
      name: 'Tech Repair Hub',
      category: 'Electronics',
      location: 'Tech District',
      requestCount: 28,
      estimatedRevenue: 3200,
      distance: '1.2 miles'
    }
  ];

  const successStories: SuccessStory[] = [
    {
      id: '1',
      merchantName: 'Artisan Bakery',
      category: 'Food & Beverage',
      requestCount: 65,
      timeToOnboard: '2 weeks',
      advocateName: 'Sarah M.',
      rewardEarned: 500
    },
    {
      id: '2',
      merchantName: 'Fitness First Gym',
      category: 'Health & Fitness',
      requestCount: 43,
      timeToOnboard: '1 week',
      advocateName: 'Mike T.',
      rewardEarned: 750
    }
  ];

  const networkStats: NetworkStats = {
    totalMerchants: 1247,
    totalAdvocates: 8934,
    totalRequests: 15678,
    successfulOnboardings: 234,
    totalRewardsEarned: 125600
  };

  const handleMerchantRequest = (merchantId: string) => {
    // Store the intent and redirect to signup
    sessionStorage.setItem('pendingMerchantRequest', merchantId);
    navigate('/signup?intent=merchant-request');
  };

  const handleSearchMerchants = () => {
    if (!searchTerm.trim()) {
      return;
    }
    
    // Store search intent and redirect to signup
    sessionStorage.setItem('pendingMerchantSearch', JSON.stringify({
      term: searchTerm,
      location: location
    }));
    navigate('/signup?intent=merchant-search');
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="space-y-4">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              <span className="block">Build the Marketplace,</span>
              <span className="block text-blue-600 mt-2">Earn Money</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Request your favorite merchants to join and earn rewards for growing the ecosystem.
              Your voice shapes where you can pay.
            </p>
          </div>

          {/* Network Stats - COMMENTED OUT - Can be re-enabled when connected to real database values */}
          {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{networkStats.totalMerchants.toLocaleString()}</div>
              <div className="text-sm text-gray-600">Merchants</div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{networkStats.totalAdvocates.toLocaleString()}</div>
              <div className="text-sm text-gray-600">Advocates</div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">{networkStats.totalRequests.toLocaleString()}</div>
              <div className="text-sm text-gray-600">Requests</div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-orange-600">${(networkStats.totalRewardsEarned / 100).toLocaleString()}</div>
              <div className="text-sm text-gray-600">Rewards Earned</div>
            </div>
          </div> */}

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/signup')} className="text-lg px-8 py-3">
              <UserPlus className="w-5 h-5 mr-2" />
              Start Advocating
            </Button>
          </div>
        </div>
      </section>

      {/* Messaging, Loyalty & Referral Rewards Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Earn While You Connect
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Build relationships, earn loyalty rewards, and get paid for bringing friends. 
              Every interaction is an opportunity to grow your network and your wallet.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Messaging Feature */}
            <Card className="border-2 border-blue-200 hover:shadow-xl transition-all">
              <CardHeader>
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <MessageSquare className="w-7 h-7 text-blue-600" />
                </div>
                <CardTitle className="text-xl">Privacy-First Messaging</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  Keep up with news and offers from businesses you actually use. All without sharing your email, phone, nor social media accounts. You stay in control.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Real-time messaging with merchants</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Transaction notifications & updates</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>No email, phone, or social media required</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Loyalty Rewards Feature */}
            <Card className="border-2 border-purple-200 hover:shadow-xl transition-all">
              <CardHeader>
                <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <Star className="w-7 h-7 text-purple-600" />
                </div>
                <CardTitle className="text-xl">Loyalty Rewards</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  The more you engage, the more you earn. Build loyalty with merchants and unlock 
                  exclusive rewards, discounts, and special offers.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Earn points with every purchase</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Unlock tiered rewards & benefits</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Redeem points for cash or discounts</span>
                  </li>
                </ul>
                <div className="pt-4 border-t">
                  <Badge className="bg-purple-600 text-white text-sm px-3 py-1">
                    Up to 5% cashback
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Referral Rewards Feature */}
            <Card className="border-2 border-green-200 hover:shadow-xl transition-all">
              <CardHeader>
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Share2 className="w-7 h-7 text-green-600" />
                </div>
                <CardTitle className="text-xl">Referral Rewards</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  Share Vaiu with friends and family. When they join and make their first transaction, 
                  you both get rewarded. Grow your network, grow your earnings.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Earn when friends sign up</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Get paid for their first transaction</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Ongoing rewards from their activity</span>
                  </li>
                </ul>
                <div className="pt-4 border-t">
                  <Badge className="bg-green-600 text-white text-sm px-3 py-1">
                    Up to $50+ per referral
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Button 
              size="lg" 
              onClick={() => navigate('/signup')} 
              className="text-lg px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Start Earning Today
            </Button>
            <p className="text-sm text-gray-500 mt-4">
              Free to join • No fees • Start earning immediately
            </p>
          </div>
        </div>
      </section>

      {/* Navigation Tabs - COMMENTED OUT */}
      {/* <section className="px-4 sm:px-6 lg:px-8 mb-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-3 bg-white/80 backdrop-blur-sm rounded-lg p-2">
            {[
              { id: 'discover', label: 'Discover Merchants', shortLabel: 'Discover', icon: Search },
              { id: 'rewards', label: 'Earn Rewards', shortLabel: 'Rewards', icon: Gift },
              { id: 'community', label: 'Join Community', shortLabel: 'Community', icon: Users }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id as any)}
                className={`flex items-center justify-center gap-2 px-4 sm:px-6 py-3 rounded-md font-medium transition-all flex-1 sm:flex-initial ${
                  activeSection === tab.id
                    ? 'bg-blue-600 text-white shadow-md scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-5 h-5 flex-shrink-0" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.shortLabel}</span>
              </button>
            ))}
          </div>
        </div>
      </section> */}

      {/* Dynamic Content Sections - COMMENTED OUT */}
      {/* <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-6xl mx-auto">
          
          {/* Merchant Discovery Section */}
          {/* {activeSection === 'discover' && (
            <div className="space-y-8">
              <div className="text-center space-y-4">
                <h2 className="text-3xl font-bold text-gray-900">Request Your Favorite Merchants</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Can't pay with Vaiu at your favorite store? Request them to join and earn rewards when they do!
                </p>
              </div>

              {/* Search Interface */}
              {/* <Card className="max-w-2xl mx-auto">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Merchant Name</label>
                      <Input
                        placeholder="e.g., Starbucks, Target, Local Coffee Shop..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="text-lg"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Location (Optional)</label>
                      <Input
                        placeholder="e.g., Downtown Seattle, 5th Avenue..."
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                      />
                    </div>
                    <Button onClick={handleSearchMerchants} className="w-full" size="lg">
                      <Search className="w-5 h-5 mr-2" />
                      Request This Merchant
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Popular Merchant Requests */}
              {/* <div className="space-y-4">
                <h3 className="text-xl font-semibold text-center">Popular Requests in Your Area</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {merchantSuggestions.map((merchant) => (
                    <Card key={merchant.id} className="hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="space-y-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-semibold text-lg">{merchant.name}</h4>
                              <p className="text-sm text-gray-600">{merchant.category}</p>
                              <p className="text-xs text-gray-500 flex items-center mt-1">
                                <MapPin className="w-3 h-3 mr-1" />
                                {merchant.location} • {merchant.distance}
                              </p>
                            </div>
                            <Badge variant="secondary">{merchant.requestCount} requests</Badge>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Potential monthly revenue:</span>
                              <span className="font-medium">${merchant.estimatedRevenue.toLocaleString()}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-green-600 h-2 rounded-full" 
                                style={{ width: `${Math.min((merchant.requestCount / 50) * 100, 100)}%` }}
                              ></div>
                            </div>
                            <p className="text-xs text-gray-500">
                              {50 - merchant.requestCount} more requests needed for outreach
                            </p>
                          </div>

                          <Button 
                            onClick={() => handleMerchantRequest(merchant.id)}
                            className="w-full"
                            variant="outline"
                          >
                            <HandHeart className="w-4 h-4 mr-2" />
                            Add My Request
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )} */}

          {/* Rewards Section */}
          {/* {activeSection === 'rewards' && (
            <div className="space-y-8">
              <div className="text-center space-y-4">
                <h2 className="text-3xl font-bold text-gray-900">Earn Meaningful Rewards</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Get rewarded for every contribution you make to growing the network. Your advocacy pays off!
                </p>
              </div>

              {/* Reward Categories */}
              {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    title: 'Merchant Requests',
                    description: 'Earn points for each merchant you request',
                    points: '10-50 points',
                    icon: Store,
                    color: 'bg-blue-100 text-blue-600'
                  },
                  {
                    title: 'Successful Onboarding',
                    description: 'Bonus when your requested merchant joins',
                    points: '500-2000 points',
                    icon: CheckCircle,
                    color: 'bg-green-100 text-green-600'
                  },
                  {
                    title: 'Referral Rewards',
                    description: 'Bring friends to the network',
                    points: '200 points',
                    icon: UserPlus,
                    color: 'bg-purple-100 text-purple-600'
                  },
                  {
                    title: 'Community Engagement',
                    description: 'Social sharing and participation',
                    points: '5-25 points',
                    icon: MessageSquare,
                    color: 'bg-orange-100 text-orange-600'
                  }
                ].map((reward, index) => (
                  <Card key={index} className="text-center">
                    <CardContent className="p-6">
                      <div className={`w-12 h-12 rounded-full ${reward.color} flex items-center justify-center mx-auto mb-4`}>
                        <reward.icon className="w-6 h-6" />
                      </div>
                      <h3 className="font-semibold mb-2">{reward.title}</h3>
                      <p className="text-sm text-gray-600 mb-3">{reward.description}</p>
                      <Badge variant="outline" className="font-bold">{reward.points}</Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Success Stories */}
              {/* <div className="space-y-4">
                <h3 className="text-xl font-semibold text-center">Recent Success Stories</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {successStories.map((story) => (
                    <Card key={story.id} className="bg-gradient-to-r from-green-50 to-blue-50">
                      <CardContent className="p-6">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold">{story.merchantName}</h4>
                            <Badge className="bg-green-600">{story.category}</Badge>
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p>• {story.requestCount} community requests</p>
                            <p>• Onboarded in {story.timeToOnboard}</p>
                            <p>• Advocate: {story.advocateName}</p>
                          </div>
                          <div className="flex items-center justify-between pt-2 border-t">
                            <span className="text-sm font-medium">Reward Earned:</span>
                            <span className="text-lg font-bold text-green-600">{story.rewardEarned} points</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )} */}

          {/* Community Section */}
          {/* {activeSection === 'community' && (
            <div className="space-y-8">
              <div className="text-center space-y-4">
                <h2 className="text-3xl font-bold text-gray-900">Join the Community</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Connect with other advocates, share strategies, and work together to grow the network in your area.
                </p>
              </div>

              {/* Community Features */}
              {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <MapPin className="w-5 h-5 text-blue-600" />
                      <span>Local Communities</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-gray-600">Join advocates in your city and neighborhood to coordinate merchant requests.</p>
                    <div className="space-y-2">
                      {['Seattle Advocates (234 members)', 'Downtown District (89 members)', 'University Area (156 members)'].map((community, index) => (
                        <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                          <span className="text-sm">{community}</span>
                          <Button size="sm" variant="outline">Join</Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Trophy className="w-5 h-5 text-yellow-600" />
                      <span>Leaderboards</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-gray-600">See top advocates and compete in friendly challenges.</p>
                    <div className="space-y-2">
                      {[
                        { name: 'Alex M.', points: 2450, rank: 1 },
                        { name: 'Sarah K.', points: 2180, rank: 2 },
                        { name: 'Mike R.', points: 1950, rank: 3 }
                      ].map((advocate) => (
                        <div key={advocate.rank} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              advocate.rank === 1 ? 'bg-yellow-100 text-yellow-800' :
                              advocate.rank === 2 ? 'bg-gray-100 text-gray-800' :
                              'bg-orange-100 text-orange-800'
                            }`}>
                              {advocate.rank}
                            </div>
                            <span className="font-medium">{advocate.name}</span>
                          </div>
                          <span className="text-sm text-gray-600">{advocate.points.toLocaleString()} pts</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )} */}
        {/* </div>
      </section> */}

    </div>
  );
};

export default MarketingSection;
